import { HKPImage } from "../../../media/HKPImage";
import React, { useRef, useState } from "react";
import Slider from "react-slick";
import { isPreviewDoc, isPreviewVideo, previewMediaUrl, videoSrcWithPreviewFrame } from "../previewMedia";

/** Every slide is a 4:5 portrait box. A photo near that shape fills it; a much wider or taller one stays whole. */
const BOX_RATIO = 4 / 5;
export const fillsBox = (width: number, height: number, boxRatio: number = BOX_RATIO): boolean => {
  if (!width || !height) return false;
  const ratio = width / height / (boxRatio || BOX_RATIO);
  return ratio >= 0.75 && ratio <= 1.25;
};

const CarouselForProduct = (props: any) => {
  // Only the slide on screen draws its blurred backdrop, and only it and its neighbours load the photo: a CSS
  // background is never lazy, and the browser's own lazy loading reaches several slides ahead, so every photo
  // downloaded the moment the certificate opened or moved.
  const [active, setActive] = useState(0);
  // Photos that must stay whole (much wider or taller than the box), keyed by slide, with the copy the image
  // actually loaded, so the blurred backdrop reuses it instead of downloading a second size.
  const [whole, setWhole] = useState<Record<number, string>>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const settings = {
    beforeChange: (current: number, next: number) => {
      // A video never keeps playing once the buyer slides away from it.
      if (next !== current) rootRef.current?.querySelectorAll("video").forEach((video) => video.pause());
      setActive(next);
    },
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    dots: true,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: false,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 0,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  const images = Array.isArray(props.images) ? props.images : [];

  return (
    <>
      <div className="CarouselForProduct" ref={rootRef}>
        <Slider {...settings}>
          {images.map((ele: any, index: number) => {
            const url = previewMediaUrl(ele);
            if (!url) {
              return null;
            }
            const openHero = () => {
              props.handleOpen({
                name: "mainLogo",
              });
              props.handleSetSelectedImage(ele);
            };
            if (isPreviewDoc(ele)) {
              const isPdf = /\.pdf(\?|$)/i.test(url);
              return (
                <div key={`${url}-${index}`} onClick={openHero}>
                  <iframe
                    title="document"
                    src={isPdf ? url : `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                  />
                </div>
              );
            }
            if (isPreviewVideo(ele)) {
              return (
                <div key={`${url}-${index}`}>
                  <div
                    className="ImageDis-bg-bg"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {/* The video on screen loads only enough to show its own first frame; the others load nothing. */}
                    <video
                      controls
                      playsInline
                      preload={index === active ? "metadata" : "none"}
                      src={videoSrcWithPreviewFrame(url)}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
              );
            }
            return (
              <div key={`${url}-${index}`} onClick={openHero}>
                <div className="ImageDis-bg-bg">
                  <div
                    style={{
                      backgroundImage: index === active && whole[index] ? `url("${whole[index]}")` : undefined,
                      position: "absolute",
                    }}
                    className="ImageDis-bg"
                  ></div>
                  {Math.abs(index - active) <= 1 ? (
                    <HKPImage
                      preset="PRODUCT_DETAIL"
                      legacyUrl={url}
                      className={`ImageDis cursor-pointer${whole[index] ? " is-whole" : ""}`}
                      alt=""
                      onLoad={(event) => {
                        const img = event.currentTarget;
                        // The box is 4:5 until 70vh caps it (short or landscape screens), so judge against its real shape.
                        const box = img.parentElement;
                        const boxRatio = box && box.clientHeight ? box.clientWidth / box.clientHeight : BOX_RATIO;
                        const keepWhole = fillsBox(img.naturalWidth, img.naturalHeight, boxRatio)
                          ? ""
                          : img.currentSrc || url;
                        setWhole((prev) => (prev[index] === keepWhole ? prev : { ...prev, [index]: keepWhole }));
                      }}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    </>
  );
};
export default CarouselForProduct;

